import {
  AppData,
  ComponentRef,
  OnFontChanged,
  FontPickerOnStyleChange,
  FontPickerOptions,
  type SelectFontFamilyOptions,
  type SelectFontWeightOptions,
  SelectTextThemeOptions,
  SelectLinkOptions,
} from '@wix/editor-platform-sdk-types';
import type {
  ColorPickerOptions,
  BackgroundPickerOptions,
} from '@wix/public-editor-platform-interfaces';
import type {
  SelectBindingPanelOptions,
  SelectFilterPanelOptions,
  SelectSortPanelOptions,
} from '@wix/editor-platform-host-integration-apis';
import { MediaManagerCategory } from '@wix/editorx-core-api';
import {
  Link,
  AppData as DMAppData,
  CompRef,
} from '@wix/document-services-types';
import {
  ColorPickerSDKOptions,
  ComponentInitialSize,
  PlatformApiMethods,
} from '@wix/editor-platform-host-integration-apis';
import { convertMediaCategory } from '../utils/media';
import type { DocumentServices } from '../types';
import type { BoundObjectRef } from '@wix/editor-platform-feature-store';
import type { EditorPointer } from '@wix/editor-document-logic-api';
import type { ContainerLayoutTransitionConfig } from '@wix/editor-platform-manifest-services';
/**
 * editor API does not care
 * about appData and token seems it is historical artifact
 */
const emptyAppData = {} as DMAppData;
const token = '';

/**
 * TODO: it feels like this class is redundant
 * we need to find a good way to get rid of it
 */
export abstract class HostAPIBase {
  constructor(
    protected readonly platformApiMethods: PlatformApiMethods,
    protected readonly documentServicesAPI: DocumentServices,
  ) {}

  editor = {
    openLanguageSupportPanel: (appDefinitionId: string) => {
      this.platformApiMethods.editor.openPanel(
        { appDefinitionId } as Parameters<
          PlatformApiMethods['editor']['openPanel']
        >[0],
        'panels.toolPanels.languageSupport',
        {},
        true,
      );
    },
    openFontsUploadPanel: (appDefinitionId: string) => {
      this.platformApiMethods.editor.openPanel(
        { appDefinitionId } as DMAppData,
        'panels.toolPanels.fontsUploadPanel',
        {},
        true,
      );
    },
    openDashboardPanel: (
      appDefinitionId: string,
      options: {
        url: string;
        closeOtherPanels?: boolean;
      },
    ) => {
      return new Promise<void>((resolve) => {
        this.platformApiMethods.editor.openDashboardPanel(
          { appDefinitionId } as DMAppData,
          token,
          { ...options, onCloseCallback: resolve },
        );
      });
    },
    openPanel: (panelId: string, props: Record<string, any>) => {
      this.platformApiMethods.editor.openPanel(emptyAppData, panelId, props);
    },
    userPreferences: {
      set: (appDefinitionId: string, preferences: Record<string, any>) => {
        return this.platformApiMethods.editor.userPreferences.site.set(
          { appDefinitionId } as DMAppData,
          token,
          preferences,
        );
      },
      get: (appDefinitionId: string, keys: string[]) => {
        return this.platformApiMethods.editor.userPreferences.site.get(
          { appDefinitionId } as DMAppData,
          token,
          keys,
        );
      },
    },
    openLinkPanel: (options: {
      value?: Link;
      showLinkTargetSection?: boolean;
    }) => {
      return this.platformApiMethods.editor.openLinkPanel(
        emptyAppData,
        token,
        options,
      );
    },
    openPropertyBindingPanel: (props: { dataItemId: string }) => {
      return this.platformApiMethods.editor.openPropertyBindingPanel?.(props);
    },
    selectMedia: async (options: {
      category?: string;
      isMultiSelect?: boolean;
    }) => {
      const { category: originalCategory, ...rest } = options;
      const category = convertMediaCategory(originalCategory);
      return this.platformApiMethods.editor.selectMedia?.({
        ...(category ? { category } : {}),
        ...rest,
      });
    },

    selectLink: (value?: Link, options?: SelectLinkOptions) => {
      return this.platformApiMethods.editor.selectLink?.(value, options);
    },

    openMediaPanel: (options: {
      mediaType: MediaManagerCategory;
      isMultiSelect: boolean;
    }) => {
      return this.platformApiMethods.editor.openMediaPanel(
        emptyAppData,
        token,
        options,
      );
    },
    openFontPicker: async (
      options: FontPickerOptions,
      onStyleChange: FontPickerOnStyleChange,
      onFontChangedV2?: OnFontChanged,
    ) => {
      await this.platformApiMethods.editor.openFontPicker(
        emptyAppData,
        token,
        options,
        onStyleChange,
        onFontChangedV2,
      );
    },
    openColorPicker: async (
      options: ColorPickerSDKOptions,
      onColorChange: (value: {
        color: string;
        theme: string;
        isHover: boolean;
      }) => void,
    ) => {
      await this.platformApiMethods.editor.openColorPicker(
        emptyAppData,
        token,
        options,
        onColorChange,
      );
    },
    selectFontFamily: (options: SelectFontFamilyOptions) =>
      this.platformApiMethods.editor.selectFontFamily?.(options),
    getFontFamilyDisplayName: (fontFamily: string) =>
      this.platformApiMethods.editor.getFontFamilyDisplayName?.(fontFamily),
    selectColor: (options: ColorPickerOptions) =>
      this.platformApiMethods.editor.selectColor?.(options),
    selectBackground: (options: BackgroundPickerOptions) =>
      this.platformApiMethods.editor.selectBackground?.(options),
    selectBinding: this.platformApiMethods.editor.selectBinding
      ? (target: BoundObjectRef, options: SelectBindingPanelOptions) =>
          this.platformApiMethods.editor.selectBinding!(target, options)
      : undefined,
    selectSort: this.platformApiMethods.editor.selectSort
      ? (target: BoundObjectRef, options: SelectSortPanelOptions) =>
          this.platformApiMethods.editor.selectSort!(target, options)
      : undefined,
    selectFilter: this.platformApiMethods.editor.selectFilter
      ? (target: BoundObjectRef, options: SelectFilterPanelOptions) =>
          this.platformApiMethods.editor.selectFilter!(target, options)
      : undefined,
    selectFontWeight: (options: SelectFontWeightOptions) =>
      this.platformApiMethods.editor.selectFontWeight?.(options),
    selectTextTheme: (options: SelectTextThemeOptions) =>
      this.platformApiMethods.editor.selectTextTheme?.(options),
    registerToolsPanel: async (
      appData: AppData,
      toolsMenuItemConfig: {
        title: string;
      },
      toolsPanelConfig: {
        url: string;
        width: number;
        height: number;
        initialPosition: {
          x: number;
          y: number;
        };
      },
    ) => {
      return this.platformApiMethods.editor.registerToolsPanelWithOpener(
        appData as unknown as DMAppData,
        toolsMenuItemConfig,
        toolsPanelConfig,
      );
    },
    generateComponentLayout: (options: {
      componentInitialSize: ComponentInitialSize;
    }) => {
      return this.platformApiMethods.editor.generateComponentLayout?.(options);
    },
    updateComponentLayout: (options: {
      editorPointer: EditorPointer<CompRef>;
      targetSize: ComponentInitialSize;
      parentPointer: EditorPointer<CompRef>;
    }) => {
      return this.platformApiMethods.editor.updateComponentLayout?.(options);
    },
    regenerateContainerLayout: (options: {
      editorPointer: EditorPointer<CompRef>;
      containerLayoutTransitionConfig: ContainerLayoutTransitionConfig;
    }) => {
      return this.platformApiMethods.editor.regenerateContainerLayout?.(
        options,
      );
    },
    selection: {
      getSelectedComponents: () => {
        return this.platformApiMethods.editor.selection.getSelectedComponents();
      },
      getSingleSelectedOrThrow: () =>
        this.platformApiMethods.editor.selection.getSingleSelectedOrThrow(),
    },
    info: {
      getEditorSessionId: () => {
        return this.platformApiMethods.editor.info.getEditorSessionId();
      },
    },
    templates: {
      hasTemplates: (editorPointer: EditorPointer<CompRef>) => {
        return (
          this.platformApiMethods.editor.hasTemplates?.(editorPointer) || false
        );
      },
    },
    states: {
      setDesignStates: (
        editorPointers: EditorPointer<CompRef>[],
        states: string[],
      ): void => {
        this.platformApiMethods.editor.setDesignStates?.(
          editorPointers,
          states,
        );
      },
      resetDesignStates: (editorPointers: EditorPointer<CompRef>[]): void => {
        this.platformApiMethods.editor.resetDesignStates?.(editorPointers);
      },
      getDesignStates: (editorPointers: EditorPointer<CompRef>[]): string[] => {
        return (
          this.platformApiMethods.editor.getDesignStates?.(editorPointers) ?? []
        );
      },
    },
  };

  viewState = {
    getViewState: (componentRef: ComponentRef) => {
      return this.platformApiMethods.editor.widgets.getViewState(
        emptyAppData,
        componentRef,
      );
    },

    setViewState: (componentRef: ComponentRef, stateName: string) => {
      this.platformApiMethods.editor.widgets.setViewState(
        emptyAppData,
        componentRef,
        stateName,
      );
    },
  };
}
